import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import DiscordLoginButton from '@/components/Auth/DiscordLoginButton'
import SimpleCaptcha from '@/components/Auth/SimpleCaptcha'
import { useNotification } from '@/hooks/useNotification'
import { useAuth } from '@/hooks/useAuth'
import { register as registerUser, ApiError } from '@/services/authService'

function RegisterPage() {
  const { login } = useAuth()
  const { showNotification } = useNotification()

  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [captchaVerified, setCaptchaVerified] = useState(false)

  const validateForm = () => {
    if (!username || !email || !password || !confirmPassword) {
      setError('Vyplňte prosím všechna pole')
      return false
    }

    if (username.length < 3) {
      setError('Uživatelské jméno musí mít alespoň 3 znaky')
      return false
    }

    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setError('Uživatelské jméno může obsahovat pouze písmena, čísla a podtržítka')
      return false
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('Zadejte platnou emailovou adresu')
      return false
    }

    if (password.length < 6) {
      setError('Heslo musí mít alespoň 6 znaků')
      return false
    }

    if (password !== confirmPassword) {
      setError('Hesla se neshodují')
      return false
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!validateForm()) {
      return
    }

    if (!captchaVerified) {
      setError('Dokončete prosím ověření')
      return
    }

    setIsSubmitting(true)

    try {
      const { user } = await registerUser({ username, email, password })
      login(user)
      showNotification('success', 'Registrace úspěšná!', 'Váš účet byl vytvořen a jste přihlášeni')
      setTimeout(() => {
        window.location.href = '/'
      }, 1200)
    } catch (err) {
      console.error('Registration error:', err)
      if (err instanceof ApiError) {
        if (err.message.toLowerCase().includes('username')) {
          setError('Toto uživatelské jméno je již obsazeno')
        } else if (err.message.toLowerCase().includes('email')) {
          setError('Tento email je již zaregistrován')
        } else {
          setError(err.message)
        }
      } else if (err instanceof Error) {
        setError(err.message)
      } else {
        setError('Registrace se nezdařila. Zkuste to prosím znovu.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="auth-page register-page">
      <div className="auth-backdrop" aria-hidden="true">
        <div className="auth-backdrop__glow auth-backdrop__glow--primary" />
        <div className="auth-backdrop__glow auth-backdrop__glow--accent" />
        <div className="auth-backdrop__grid" />
      </div>

      <div className="container">
        <div className="auth-grid">
          <div className="auth-copy">
            <span className="auth-badge">Allcom Hub</span>
            <h1>Vytvořte si Allcom účet</h1>
            <p className="auth-description">
              Připojte se k největší česko-slovenské Allcom komunitě a získejte přístup k prémiovým funkcím, akcím a komunitním výhodám.
            </p>

            <ul className="auth-highlights">
              <li>
                <span className="auth-highlights__icon" aria-hidden="true">🎯</span>
                <div>
                  <h3>Personalizovaný profil</h3>
                  <p>Sledujte své statistiky, sbírejte mince a mějte přehled o své aktivitě.</p>
                </div>
              </li>
              <li>
                <span className="auth-highlights__icon" aria-hidden="true">🛡️</span>
                <div>
                  <h3>Bezpečné přihlášení</h3>
                  <p>Silné zabezpečení, dvoufázové ověření a snadné propojení s Discordem.</p>
                </div>
              </li>
              <li>
                <span className="auth-highlights__icon" aria-hidden="true">✨</span>
                <div>
                  <h3>Komunitní výhody</h3>
                  <p>Získejte exkluzivní role, přístup k soukromým kanálům a novinky mezi prvními.</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="auth-card">
            <div className="auth-card__header">
              <h2>Registrace do Allcom</h2>
              <p>Nejrychlejší cesta k připojení do Allcom komunity a jejím výhodám.</p>
            </div>

            <DiscordLoginButton className="discord-btn-large" text="Registrovat přes Discord" />

            <div className="divider">
              <span>NEBO</span>
            </div>

            <form onSubmit={handleSubmit} className="auth-card__form register-form">
              <div className="form-group">
                <label htmlFor="username">Uživatelské jméno *</label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Zadejte uživatelské jméno"
                  disabled={isSubmitting}
                  autoComplete="username"
                  required
                />
                <span className="form-hint">Alespoň 3 znaky, pouze písmena, čísla a podtržítka</span>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vas@email.cz"
                  disabled={isSubmitting}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Heslo *</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Zadejte heslo"
                  disabled={isSubmitting}
                  autoComplete="new-password"
                  required
                />
                <span className="form-hint">Alespoň 6 znaků</span>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Potvrzení hesla *</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Zadejte heslo znovu"
                  disabled={isSubmitting}
                  autoComplete="new-password"
                  required
                />
              </div>

              <div className="form-group captcha-group">
                <SimpleCaptcha onVerify={(valid) => setCaptchaVerified(valid)} />
              </div>

              {error && <div className="form-error">{error}</div>}

              <button
                type="submit"
                className={`btn btn--primary ${isSubmitting ? 'is-loading' : ''}`}
                disabled={isSubmitting || !captchaVerified}
              >
                <span className="btn-label">
                  {isSubmitting && (
                    <span className="loading-dots" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </span>
                  )}
                  <span>{isSubmitting ? 'Registruji...' : 'Vytvořit účet'}</span>
                </span>
              </button>
            </form>

            <div className="auth-card__footer login-link">
              Již máte účet?{' '}
              <Link to="/login">Přihlaste se</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
